import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import SituationBox from "./SituationBox.jsx";
import ResultForm from "../Components/ResultForm.jsx";
import ResultFormMobile from "../Components/ResultFormMobile.jsx";
import HeightInterval from "../Components/HeightInterval.jsx";
import Alert from "../Components/Alert.jsx";
import config from "../config.js";
import {
  Desktop,
  LargeTablet,
  SmallTablet,
  Mobile,
} from "../MediaQuery/QueryPageMediaQuery.js";
import useStore from "../store.js";
import ReAskForm from "./ReAskForm.jsx";
import CaseFeedback from "./CaseFeedback.jsx";
import ModalUserFeedback from "../Components/Modal/ModalUserFeedback.jsx";
import ModalThankLowGrant from "../Components/Modal/ModalThankLowGrant.jsx";
import CaseSummarySection from "./CaseSummarySection.jsx";
import SuggestedAskCards from "../Components/SuggestedAskCards.jsx";
import { trackNaverViewContent } from "../utils/naverConversion.js";
import ResultSummaryPreview from "./ResultSummaryPreview.jsx";
import { shouldRequestResultFeedback } from "../utils/userFeedbackPrompt.js";
import PromptDock from "./PromptExport/PromptDock.jsx";
import { PROMPT_EXPORT_ENABLED } from "../utils/promptExportApi.js";
import useVisibleTrigger from "../hooks/useVisibleTrigger.js";

const getCompletedLogId = (data) => (
  data?.data?.description?.log_id ||
  data?.description?.log_id ||
  data?.data?.log_id ||
  data?.log_id ||
  null
);

/* Result는 Desktop, Mobile을 동시에 만들고, 
ResultForm을 두 버전 따로 만듦 */
const Result = ({ resultId, onlySituationBox }) => {
  // resultId는 waiting일 수도 있고, 질문 결과 log_id일 수도 있음
  // 이때 log_id는 SSE 응답 event: Query Complete에 있음
  const navigate = useNavigate();

  const [inquiries, setInquiries] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [recommendedQuestions, setRecommendedQuestions] = useState([]);
  const [summaryPoints, setSummaryPoints] = useState('');
  const { situation, setSituation, setShowCopyCaseSnackbar, textValue, queryCompleteData } = useStore();
  /* ReAskForm의 높이를 설정하기 위함 */
  const [situationBoxHeight, setSituationBoxHeight] = useState(0);
  const situationBoxRef = useRef(null);
  /* 유저 피드백 ModalUserFeedback 모달창 띄우기 */
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  /* ModalUserFeedback 피드백 제출 완료 여부 */
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loadedResultId, setLoadedResultId] = useState(null);
  const checkedFeedbackResultIds = useRef(new Set());
  const [feedbackTrigger, setFeedbackTrigger] = useState(null);
  const feedbackRequestVersionRef = useRef(0);
  const [error, setError] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [resultView, setResultView] = useState("legacy");
  const [resultPreview, setResultPreview] = useState(null);
  const [feedbackPrompt, setFeedbackPrompt] = useState(null);
  const [promptExportPurchased, setPromptExportPurchased] = useState(false);
  const trackedContentIds = useRef(new Set());

  useEffect(() => {
    setShowFeedbackModal(false);
  }, [resultId]);

  useEffect(() => {
    feedbackRequestVersionRef.current += 1;

    return () => {
      feedbackRequestVersionRef.current += 1;
    };
  }, [resultId]);

  const isFeedbackTriggerEnabled =
    resultId !== "waiting" &&
    loadedResultId === resultId &&
    !onlySituationBox &&
    showResult &&
    inquiries.length > 0 &&
    !checkedFeedbackResultIds.current.has(resultId);

  const requestFeedbackIfMissing = useCallback(async () => {
    if (checkedFeedbackResultIds.current.has(resultId)) return true;

    checkedFeedbackResultIds.current.add(resultId);
    const requestVersion = feedbackRequestVersionRef.current;
    const shouldShow = await shouldRequestResultFeedback();
    if (requestVersion !== feedbackRequestVersionRef.current) return true;

    if (shouldShow) {
      setShowFeedbackModal(true);
      return true;
    }
    if (shouldShow === null) {
      // 일시적인 조회 실패라면 즉시 조건 또는 마지막 판례 도달 시 다시 확인한다.
      checkedFeedbackResultIds.current.delete(resultId);
      return false;
    }

    return true;
  }, [resultId]);

  useVisibleTrigger({
    enabled: isFeedbackTriggerEnabled,
    target: feedbackTrigger,
    onVisible: requestFeedbackIfMissing,
  });

  useEffect(() => {
    const completedLogId = getCompletedLogId(queryCompleteData);
    const isFreshCompletedResult =
      completedLogId != null && String(completedLogId) === String(resultId);

    if (
      !isFreshCompletedResult ||
      loadedResultId !== resultId ||
      onlySituationBox ||
      inquiries.length === 0 ||
      (
        feedbackPrompt?.mandatory_review_pending !== true &&
        feedbackPrompt?.first_paid_search !== true
      ) ||
      checkedFeedbackResultIds.current.has(resultId)
    ) {
      return;
    }

    requestFeedbackIfMissing();
  }, [
    feedbackPrompt,
    inquiries.length,
    loadedResultId,
    onlySituationBox,
    queryCompleteData,
    requestFeedbackIfMissing,
    resultId,
  ]);

  // 스낵바는 Mobile에만 우선 적용하기 위해 위해 현재 화면의 가로 크기 가져오기
  const screenWidth = window.innerWidth;

  // 렌더링 완료 후, Ref를 통해 SituationBox DOM 요소에 접근하여 높이를 설정
  useEffect(() => {
    if (situationBoxRef.current) {
      setSituationBoxHeight(situationBoxRef.current.offsetHeight); // SituationBox 높이를 가져옴
    }
    // Ref가 설정된 후 혹은 질문 결과 id과 inquiries 달라지면 실행
  }, [resultId, inquiries, queryCompleteData]);

  useEffect(() => {
    // waiting 상태일 때는 store의 textValue를 사용하고 API 요청하지 않음
    if (resultId === "waiting") {
      setLoadedResultId(null);
      setShowFeedbackModal(false);
      setShowSummary(false);
      setShowAlert(false);
      setShowResult(false);
      setResultView("legacy");
      setResultPreview(null);
      setFeedbackPrompt(null);
      return;
    }

    // Query Complete 이벤트가 있어도 항상 API로 전체 데이터를 가져와야 함

    const SERVER_URI = config.serverUri;
    const uri = `/private/logs/${resultId}`;
    const url = `${SERVER_URI}${uri}`;

    const timers = [];
    let isActive = true;
    setResultView("legacy");
    setResultPreview(null);
    setFeedbackPrompt(null);
    setLoadedResultId((currentResultId) => (
      currentResultId === resultId ? currentResultId : null
    ));
    const fetchLog = async () => {
      try {
        const response = await fetch(url, {
          credentials: "include",
        });

        if (!response) {
          throw new Error("Failed to fetch Result");
        }
        if (response.status === 404) {
          if (!isActive) return;
          setInquiries([]);
          setLoadedResultId(null);
          setShowSummary(false);
          setShowAlert(false);
          setShowResult(true);
          setError("not-found");
          return;
        }
        if (response.status === 400 || response.status === 500) {
          alert("잘못된 접근입니다. 페이지를 이동합니다");
          navigate("/main");
          return;
        }
        if (!response.ok) {
          throw new Error("Failed to fetch Result");
        }

        const data = await response.json();
        if (!isActive) return;
        setRecommendedQuestions(data.recommended_questions || []);
        setSummaryPoints(data.batch_summary?.summaryPoints || '');
        setResultView(data.result_view || "legacy");
        setResultPreview(data.result_preview || null);
        setFeedbackPrompt(data.feedback_prompt || null);
        setPromptExportPurchased(Boolean(data.prompt_export_purchased));
        updateInquiries(data.questions || [], data.result_view === "summary_preview_v1");
        if (data.questions?.[0]?.content) {
          setSituation(data.questions[0].content);
        }
        setLoadedResultId(resultId);
        setError(false);

        setShowAlert(false);
        if (data.result_view === "summary_preview_v1") {
          // 원문 근거 링크와 이동할 판례를 같은 렌더링에서 준비한다.
          setShowSummary(true);
          setShowResult(true);
        } else {
          setShowSummary(false);
          setShowResult(false);
          timers.push(setTimeout(() => setShowSummary(true), 300));
          timers.push(setTimeout(() => setShowAlert(true), 900));
          timers.push(setTimeout(() => setShowResult(true), 1500));
        }
      } catch (error) {
        if (!isActive) return;
        console.error("Error catching Result Error:", error);
        setInquiries([]);
        setLoadedResultId(null);
        setError(true);
        setShowSummary(false);
        setShowAlert(false);
        setShowResult(true);
      }
    };

    fetchLog();

    return () => {
      isActive = false;
      timers.forEach(clearTimeout);
    };
  }, [navigate, queryCompleteData, resultId, setSituation]);

  useEffect(() => {
    // 항상 최신 질문 내용(textValue)이 SituationBox에 반영되도록 설정
    if (resultId === "waiting") {
      setSituation(textValue);
    }
  }, [resultId, textValue, setSituation]);

  useEffect(() => {
    if (onlySituationBox || resultId === "waiting" || loadedResultId !== resultId || error || !showResult || inquiries.length === 0) return;
    if (trackedContentIds.current.has(resultId)) return;

    trackedContentIds.current.add(resultId);
    trackNaverViewContent({ id: resultId });
  }, [resultId, loadedResultId, error, showResult, onlySituationBox, inquiries.length]);

  function updateInquiries(questions, preserveSourceOrder) {
    if (!Array.isArray(questions)) {
      console.error("Invalid questions data:", questions);
      return;
    }
    // Preview source indices refer to the server order, including tied scores.
    const ordered = [...questions];
    if (!preserveSourceOrder) ordered.sort((a, b) => b.relevance - a.relevance);
    setInquiries(ordered);
  }

  /* SituationBox에서 버튼이 눌렸을 때 처리 */
  const handleCopyClick = () => {
    const textToCopy = `${situation}`;
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        // 화면 해상도가 1140px 이하일 때(Mobile, Tablet) 스낵바 사용
        if (screenWidth <= 1140) {
          setShowCopyCaseSnackbar(true);
          // 3초 후 애니메이션이 시작되고, 애니메이션이 완료된 후 사라지게 설정
          setTimeout(() => {
            setShowCopyCaseSnackbar(false);
          }, 6000); // 3초 유지 + 3초 동안 애니메이션 실행 = 총 6초 후 상태 변경
        } else {
          // 해상도가 1140px 보다 클 때(Desktop) window.alert 사용
          alert("판례 내용이 복사되었습니다");
        }
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  const handleRewriteClick = () => {
    setIsEditing(true);
  };

  // URL이 변경될 때 isEditing 상태를 false로 초기화
  useEffect(() => {
    setIsEditing(false);
  }, [resultId]);

  // 현재 디바이스 타입을 결정하는 로직
  const getDeviceType = () => {
    const width = window.innerWidth;
    if (width >= 1141) return 'desktop';
    if (width >= 745) return 'largeTablet';
    if (width >= 481) return 'smallTablet';
    return 'mobile';
  };

  // 현재 디바이스 타입에 따른 렌더링
  const renderContent = () => {
    const deviceType = getDeviceType();
    const isSummaryPreview = resultView === "summary_preview_v1";
    
    // waiting 상태일 때는 textValue를, log_id 상태일 때는 store의 situation을 사용
    const situationContent = resultId === "waiting" ? textValue : situation;
    
    const situationBoxContent = !error && (
      <>
        {isEditing ? (
          <ReAskForm
            height={situationBoxHeight}
            initialTextValue={situationContent || ''}
            onClose={() => setIsEditing(false)}
          />
        ) : (
          <SituationBox
            ref={situationBoxRef}
            title="내 상황"
            titleFontSize={16}
            intervalHeight={10}
            contents={situationContent || ''}
            type="result"
            onCopyClick={handleCopyClick}
            onRewriteClick={handleRewriteClick}
          />
        )}
        <HeightInterval height={16} />
      </>
    );

    const feedbackContent = inquiries.map((inquiry, index) => {
      const caseCard = deviceType === 'mobile' ? (
        <ResultFormMobile
          index={index}
          anchorId={`case-result-${index + 1}`}
          courtName={inquiry.courtName}
          sentencingData={inquiry.sentencingData}
          caseNumber={inquiry.caseNumber}
          caption={inquiry.caption}
          relevance={inquiry.relevance}
          question={inquiry.question}
          detail={inquiry.detail}
          answer={inquiry.answer}
          caseId={inquiry.id}
          isComparison={inquiry.isComparison}
          commonality={inquiry.commonality}
          difference={inquiry.difference}
        />
      ) : (
        <ResultForm
          index={index}
          anchorId={`case-result-${index + 1}`}
          courtName={inquiry.courtName}
          sentencingData={inquiry.sentencingData}
          caseNumber={inquiry.caseNumber}
          caption={inquiry.caption}
          relevance={inquiry.relevance}
          question={inquiry.question}
          detail={inquiry.detail}
          answer={inquiry.answer}
          caseId={inquiry.id}
          isComparison={inquiry.isComparison}
          commonality={inquiry.commonality}
          difference={inquiry.difference}
        />
      );

      return (
        <React.Fragment key={inquiry.id || index}>
          {caseCard}
          <HeightInterval height={4} />
          <CaseFeedback
            id={inquiry.id}
            logId={inquiry.logID}
            isLike={inquiry.isLike}
          />
          {index === 0 && recommendedQuestions.length > 0 && (
            <>
              <SuggestedAskCards
                recommendedQuestions={recommendedQuestions}
                sourceLogId={isSummaryPreview ? resultId : null}
              />
            </>
          )}
        </React.Fragment>
      );
    });

    const errorMessage = error && (
      <>
        <HeightInterval height={20} />
        <ErrorMessage>
          {error === "not-found" ? (
            <>
              검색 결과를 찾을 수 없습니다.<br />
              질문 내역에서 다른 결과를 선택해주세요.
            </>
          ) : (
            <>
              죄송합니다. <br /> 요청하신 내용을 처리하는 중에 오류가 발생했습니다.<br /> 재시도 해주십시오.
            </>
          )}
        </ErrorMessage>
        <HeightInterval height={20} />
      </>
    );

    const animatedContent = (
      <>
        {(isSummaryPreview || recommendedQuestions.length > 0) && !error && showSummary && (
          <AnimatedBox>
            {isSummaryPreview ? (
              <ResultSummaryPreview
                resultId={resultId}
                preview={resultPreview}
                caseCount={inquiries.length}
                inquiries={inquiries}
              />
            ) : (
              <>
                <CaseSummarySection summaryPoints={summaryPoints} />
                <HeightInterval height={48} />
              </>
            )}
          </AnimatedBox>
        )}
        {showAlert && !isSummaryPreview && (
          <AnimatedBox>
            <Alert />
            <HeightInterval height={40} />
          </AnimatedBox>
        )}
        {showResult && (
          <AnimatedBox style={isSummaryPreview ? { animation: "none" } : undefined}>
            {errorMessage}
            {!error && feedbackContent}
            {!error && inquiries.length > 0 && (
              <FeedbackTriggerMarker
                ref={setFeedbackTrigger}
                data-testid="feedback-trigger-marker"
                aria-hidden="true"
              />
            )}
          </AnimatedBox>
        )}
      </>
    );

    switch (deviceType) {
      case 'desktop':
        return (
          <Desktop>
            <Wrapper>
              <HeightInterval height={36} />
              {situationBoxContent}
              {resultId !== "waiting" && animatedContent}
            </Wrapper>
          </Desktop>
        );
      case 'largeTablet':
        return (
          <LargeTablet>
            <Wrapper>
              {situationBoxContent}
              {resultId !== "waiting" && animatedContent}
            </Wrapper>
          </LargeTablet>
        );
      case 'smallTablet':
        return (
          <SmallTablet>
            <Wrapper>
              {situationBoxContent}
              {resultId !== "waiting" && animatedContent}
            </Wrapper>
          </SmallTablet>
        );
      case 'mobile':
        return (
          <Mobile>
            {resultId !== "waiting" && animatedContent}
          </Mobile>
        );
      default:
        return null;
    }
  };

  if (onlySituationBox) {
    if (error === "not-found") return null;
    // waiting일 때는 store의 textValue를 상황 내용으로 사용
    const situationContent =
      resultId === "waiting"
        ? textValue
        : inquiries.length > 0
        ? inquiries[0].content
        : "";
    return (
      <Wrapper>
        <HeightInterval height={36} />
        {isEditing ? (
          <ReAskForm
            height={situationBoxHeight}
            initialTextValue={situationContent || ''}
            onClose={() => setIsEditing(false)}
          />
        ) : (
          <SituationBox
            ref={situationBoxRef}
            title="내 상황"
            titleFontSize={16}
            intervalHeight={10}
            contents={situationContent || ''}
            type="result"
            onCopyClick={handleCopyClick}
            onRewriteClick={handleRewriteClick}
          />
        )}
      </Wrapper>
    );
  }

  return (
    <>
      {isSubmitted && (
        <ModalThankLowGrant
          isOpen={isSubmitted}
          onConfirm={() => setIsSubmitted(false)}
          messageTitle="소중한 피드백에 감사드립니다!"
          messageSubtitle="감사의 의미로 5로우를 지급했습니다."
        />
      )}
      {showFeedbackModal && (
        <ModalUserFeedback
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={() => {
            setIsSubmitted(true);
          }}
        />
      )}
      {renderContent()}
      {PROMPT_EXPORT_ENABLED &&
        resultId !== "waiting" &&
        loadedResultId === resultId &&
        showResult &&
        inquiries.length > 0 && inquiries.length <= 5 &&
        (inquiries.length === 5 || process.env.REACT_APP_PROMPT_EXPORT_PARTIAL_RESULTS_ENABLED === "true") && (
          <PromptDock logId={resultId} initialPurchased={promptExportPurchased} caseCount={inquiries.length} />
        )}
    </>
  );
};

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ErrorMessage = styled.div`

  color: rgba(0, 0, 0, 0.60);
  text-align: center;
  font-size: 20px;
  font-weight: 500;
  line-height: 30px;
  margin: 70px 24px;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 24px;
    margin: 30px 24px; 
  } 
`;

const AnimatedBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: fadeIn 0.5s;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(30px);}
    to { opacity: 1; transform: translateY(0);}
  }
`;

const FeedbackTriggerMarker = styled.div`
  width: 100%;
  height: 1px;
  pointer-events: none;
`;

export default Result;
